import { useApp } from 'context/app-context'
import useApi from 'hooks/use-api'
import { useCallback, useEffect, useState } from 'react'
import useSWRImmutable from 'swr/immutable'

const removeOldestCached = (mostRecent, fileCacheMap) => {
  if (fileCacheMap.size > 5) {
    let accessedAt = mostRecent
    let earliest
    fileCacheMap.forEach((val, key) => {
      if (val.accessed < accessedAt) {
        accessedAt = val.accessed
        earliest = key
      }
    })
    if (fileCacheMap.has(earliest)) {
      fileCacheMap.delete(earliest)
    }
  }
}

const parseBlob = async (blob) => {
  if (typeof blob === 'string') {
    return blob
  }

  return JSON.parse(await blob.text())
}

/**
 * Hook to hit an api endpoint and stream an s3 object to json
 * Used to stream large files directly down to the browser.
 * Maintains a very limited cache of recently fetched and parsed objects
 * @param key api endpoint to stream object from
 * @returns json object
 */
export const useStreamS3ObjectToAppFileCache = (key: string) => {
  const [text, setText] = useState(null)
  const controller = new AbortController()
  const signal = controller.signal

  const api = useApi()
  const { fileCache } = useApp()

  const { data, error, isLoading } = useSWRImmutable(
    key,
    () => api(key, 'GET', { signal }, 'application/octet-stream'),
    {
      onErrorRetry: async (error, key, config, revalidate, { retryCount }) => {
        const parseError = await parseBlob(error)
        // Never retry on streaming files that can't be found or are not authorized.
        if (
          parseError?.error?.includes('Unauthorized') ||
          parseError?.error?.includes('Not Found')
        ) {
          return
        }

        // Only retry up to 3 times.
        if (retryCount >= 3) {
          return
        }

        // Retry after 5 seconds.
        setTimeout(() => revalidate({ retryCount }), 5000)
      },
    },
  )

  const parseToCache = useCallback(async () => {
    if (signal.aborted) {
      return
    }

    // set local hook state from cache
    if (fileCache.has(key)) {
      setText(fileCache.get(key).data)
      return
    }

    if (!data || error) {
      return
    }

    // parse the data, set the local state and update our cache map
    const jsonData = await parseBlob(data)
    const nowUnix = new Date(Date.now()).valueOf()
    setText(jsonData)
    fileCache.set(key, { accessed: nowUnix, data: jsonData })
    removeOldestCached(nowUnix, fileCache)
  }, [fileCache, key, data, error, signal])

  useEffect(() => {
    setText(null)
    if (!isLoading) {
      parseToCache()
    }
  }, [data, error, isLoading])

  return {
    data: text,
    error,
    isLoading,
  }
}
